import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { Heading } from "../../design/heading";
import { Text } from "../../design/text";
import { Button } from "../../design/button";
import { Input } from "../../design/input";
import { Avatar } from "../../design/avatar";
import { Badge } from "../../design/badge";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "../../design/dialog";
import { useAccount } from "../../contexts/AccountContext";
import { useAuth } from "../../contexts/AuthContext";
import { useParent } from "../../contexts/ParentContext";
import { useSupervisor } from "../../contexts/SupervisorContext";
import MessagingService from "../../services/MessagingService";
import { Conversation, Message } from "@visitproof/types";
import { getInitials } from "../../utils/string";
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PlusIcon,
  UserIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";

const MessagesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { account } = useAccount();
  const { user, claims } = useAuth();
  const { parents } = useParent();
  const { supervisors } = useSupervisor();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  const getDisplayName = (conv: Conversation) => conv.participantName || conv.parentName || "Unknown";

  // Subscribe to conversations
  useEffect(() => {
    if (!account?.id) return;
    const unsubscribe = MessagingService.subscribeToConversations(
      account.id,
      (convs) => { setConversations(convs); setLoading(false); },
      () => setLoading(false),
    );
    return () => unsubscribe();
  }, [account?.id]);

  // Handle ?parentId= query param
  useEffect(() => {
    const parentId = searchParams.get("parentId");
    if (!parentId || !account?.id || !user?.uid || loading) return;

    const existing = conversations.find((c) => c.parentId === parentId);
    if (existing) { setSelectedConversationId(existing.id); return; }

    const create = async () => {
      try {
        const parentDoc = await getDoc(doc(db, "parents", parentId));
        if (!parentDoc.exists()) return;
        const parent = parentDoc.data();
        const conversationId = await MessagingService.getOrCreateConversation({
          accountId: account.id,
          type: "parent",
          recordId: parentId,
          userId: parent.userId || undefined,
          name: `${parent.firstName} ${parent.lastName}`,
          currentUserId: user.uid,
          caseId: parent.caseId || undefined,
        });
        setSelectedConversationId(conversationId);
      } catch (error) {
        console.error("Failed to create conversation:", error);
      }
    };
    create();
  }, [searchParams, account?.id, user?.uid, loading, conversations]);

  // Subscribe to messages
  useEffect(() => {
    if (!selectedConversationId) { setMessages([]); return; }
    const unsubscribe = MessagingService.subscribeToMessages(selectedConversationId, (msgs) => {
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    });
    if (user?.uid) MessagingService.markAsRead(selectedConversationId, user.uid);
    return () => unsubscribe();
  }, [selectedConversationId, user?.uid]);

  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || !selectedConversationId || !user || !selectedConversation) return;
    setSending(true);
    try {
      const role = claims?.role === "parent" ? "parent" : claims?.role === "supervisor" ? "supervisor" : "admin";
      await MessagingService.sendMessage({
        conversationId: selectedConversationId,
        senderId: user.uid,
        senderName: user.displayName || user.email || "Unknown",
        senderRole: role as "admin" | "supervisor" | "parent",
        body: newMessage.trim(),
        accountId: account.id,
        participantIds: selectedConversation.participantIds || [],
      });
      setNewMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSending(false);
    }
  }, [newMessage, selectedConversationId, user, selectedConversation, claims, account]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleStartConversation = async (type: "parent" | "supervisor", recordId: string, name: string, userId?: string) => {
    if (!account?.id || !user?.uid) return;
    try {
      const conversationId = await MessagingService.getOrCreateConversation({
        accountId: account.id,
        type,
        recordId,
        userId,
        name,
        currentUserId: user.uid,
      });
      setSelectedConversationId(conversationId);
      setNewConversationOpen(false);
      setSearchQuery("");
    } catch (error) {
      console.error("Failed to create conversation:", error);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) { case "admin": return "blue"; case "supervisor": return "green"; default: return "zinc"; }
  };

  const getUnreadCount = (conv: Conversation) => {
    if (!user?.uid || !conv.unreadCount) return 0;
    return conv.unreadCount[user.uid] || 0;
  };

  // Filter contacts for new conversation dialog
  const filteredParents = parents.filter((p) => {
    const name = `${p.firstName} ${p.lastName}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const filteredSupervisors = supervisors.filter((s) => {
    const name = `${s.firstName} ${s.lastName}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
        <Heading>Messages</Heading>
        <Button onClick={() => setNewConversationOpen(true)}>
          <PlusIcon className="w-4 h-4" />
          New Conversation
        </Button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Conversation List */}
        <div className="w-80 border-r border-zinc-200 dark:border-zinc-700 flex flex-col bg-white dark:bg-zinc-900">
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center"><Text className="text-zinc-400">Loading...</Text></div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center">
                <ChatBubbleLeftRightIcon className="w-10 h-10 mx-auto text-zinc-300 dark:text-zinc-600 mb-2" />
                <Text className="text-zinc-500 dark:text-zinc-400 text-sm">No conversations yet</Text>
                <Text className="text-zinc-400 dark:text-zinc-500 text-xs mt-1">Click "New Conversation" to start one</Text>
              </div>
            ) : (
              conversations.map((conv) => {
                const unread = getUnreadCount(conv);
                const isSelected = conv.id === selectedConversationId;
                const name = getDisplayName(conv);
                const convType = conv.type || "parent";

                return (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConversationId(conv.id)}
                    className={`w-full p-3 text-left border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors ${
                      isSelected ? "bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="size-10 flex-shrink-0" initials={getInitials(name)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <Text className="font-medium text-sm text-zinc-900 dark:text-white truncate">{name}</Text>
                            <Badge color={convType === "supervisor" ? "green" : "zinc"} className="text-[10px] py-0">
                              {convType === "supervisor" ? "Monitor" : "Parent"}
                            </Badge>
                          </div>
                          {unread > 0 && <Badge color="blue" className="ml-2 text-xs">{unread}</Badge>}
                        </div>
                        {conv.lastMessage && (
                          <Text className="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">
                            {conv.lastMessageBy && <span className="font-medium">{conv.lastMessageBy}: </span>}
                            {conv.lastMessage}
                          </Text>
                        )}
                        {conv.lastMessageAt && (
                          <Text className="text-xs text-zinc-400 dark:text-zinc-500 mt-0.5">
                            {moment((conv.lastMessageAt as any).toDate?.() || conv.lastMessageAt).fromNow()}
                          </Text>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Message Thread */}
        <div className="flex-1 flex flex-col bg-zinc-50 dark:bg-zinc-950">
          {!selectedConversationId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ChatBubbleLeftRightIcon className="w-16 h-16 mx-auto text-zinc-300 dark:text-zinc-600 mb-4" />
                <Text className="text-zinc-500 dark:text-zinc-400">Select a conversation or start a new one</Text>
              </div>
            </div>
          ) : (
            <>
              {/* Thread Header */}
              <div className="flex items-center gap-3 px-6 py-3 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700">
                <Avatar className="size-8" initials={getInitials(getDisplayName(selectedConversation!))} />
                <div>
                  <Text className="font-medium text-zinc-900 dark:text-white">{getDisplayName(selectedConversation!)}</Text>
                  <Text className="text-xs text-zinc-500">
                    {selectedConversation?.type === "supervisor" ? "Monitor" : "Parent"}
                  </Text>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <Text className="text-zinc-400 dark:text-zinc-500 text-sm">No messages yet. Send the first message below.</Text>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.senderId === user?.uid;
                    return (
                      <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                        <div className="max-w-[70%]">
                          <div className={`flex items-center gap-1.5 mb-1 ${isMe ? "justify-end" : ""}`}>
                            <Text className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{msg.senderName}</Text>
                            <Badge color={getRoleColor(msg.senderRole)} className="text-[10px] py-0">{msg.senderRole}</Badge>
                          </div>
                          <div className={`px-4 py-2.5 rounded-2xl ${
                            isMe
                              ? "bg-blue-600 text-white rounded-br-md"
                              : "bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white border border-zinc-200 dark:border-zinc-700 rounded-bl-md"
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                          </div>
                          <Text className={`text-[10px] text-zinc-400 mt-1 ${isMe ? "text-right" : ""}`}>
                            {(msg.createdAt as any)?.toDate ? moment((msg.createdAt as any).toDate()).format("h:mm A") : ""}
                          </Text>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-4 py-3 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700">
                <div className="flex items-end gap-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800 px-4 py-2.5 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{ maxHeight: "120px" }}
                    onInput={(e) => {
                      const t = e.target as HTMLTextAreaElement;
                      t.style.height = "auto";
                      t.style.height = Math.min(t.scrollHeight, 120) + "px";
                    }}
                  />
                  <Button color="blue" onClick={handleSend} disabled={sending || !newMessage.trim()} className="rounded-xl px-4">
                    <PaperAirplaneIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={newConversationOpen} onClose={() => { setNewConversationOpen(false); setSearchQuery(""); }} size="lg">
        <DialogTitle>New Conversation</DialogTitle>
        <DialogBody>
          <Input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mb-4"
          />

          <div className="max-h-96 overflow-y-auto space-y-1">
            {/* Parents */}
            {filteredParents.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <UserIcon className="w-4 h-4 text-zinc-400" />
                  <Text className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Parents</Text>
                </div>
                {filteredParents.map((parent) => (
                  <button
                    key={parent.id}
                    onClick={() => handleStartConversation("parent", parent.id, `${parent.firstName} ${parent.lastName}`, parent.userId)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-left"
                  >
                    <Avatar className="size-8" initials={getInitials(`${parent.firstName} ${parent.lastName}`)} />
                    <div className="flex-1 min-w-0">
                      <Text className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                        {parent.firstName} {parent.lastName}
                      </Text>
                      {parent.email && (
                        <Text className="text-xs text-zinc-500 truncate">{parent.email}</Text>
                      )}
                    </div>
                    <Badge color="zinc" className="text-[10px]">Parent</Badge>
                  </button>
                ))}
              </>
            )}

            {/* Supervisors */}
            {filteredSupervisors.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-2 py-1.5 mt-2">
                  <ShieldCheckIcon className="w-4 h-4 text-zinc-400" />
                  <Text className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Monitors</Text>
                </div>
                {filteredSupervisors.map((supervisor) => (
                  <button
                    key={supervisor.id}
                    onClick={() => handleStartConversation("supervisor", supervisor.id, `${supervisor.firstName} ${supervisor.lastName}`, supervisor.userId)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-left"
                  >
                    <Avatar className="size-8" src={supervisor.avatarUrl} initials={getInitials(`${supervisor.firstName} ${supervisor.lastName}`)} />
                    <div className="flex-1 min-w-0">
                      <Text className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                        {supervisor.firstName} {supervisor.lastName}
                      </Text>
                      {supervisor.email && (
                        <Text className="text-xs text-zinc-500 truncate">{supervisor.email}</Text>
                      )}
                    </div>
                    <Badge color="green" className="text-[10px]">Monitor</Badge>
                  </button>
                ))}
              </>
            )}

            {filteredParents.length === 0 && filteredSupervisors.length === 0 && (
              <div className="py-8 text-center">
                <Text className="text-zinc-500">
                  {searchQuery ? "No contacts match your search" : "No contacts available"}
                </Text>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => { setNewConversationOpen(false); setSearchQuery(""); }}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
